import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CustomersFacade } from '@domain/application/facade';
import { LegalRepresentService } from '@domain/application/observable-services/legal-representative.service';
import { SessionService } from '@domain/application/observable-services/session.service';
import { TariffType } from '@domain/model/enums/currency/tariff-type.enum';
import { DocumentTypes } from '@domain/model/enums/document-type/cmp-link';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { RouterService } from '@modules/business-accounts/core/router/router.service';
import { IBaseFormBuilder } from '@modules/business-accounts/forms/utils/builders/base-builder.class';
import { ConfigLegalPersonBuilder } from '@modules/business-accounts/forms/utils/config-step/builders/config-legal-person-builder.class';
import { ConfigurationStepDirector } from '@modules/business-accounts/forms/utils/config-step/director.class';
import { DataCollectionFormBuilder } from '@modules/business-accounts/forms/utils/data-collection/builders/data-collection-builder.class';
import { DataCollectionpDirector } from '@modules/business-accounts/forms/utils/data-collection/director.class';
import { LegalPersonDataClientFormBuilder } from '@modules/business-accounts/forms/utils/data-step/legal-person/builders/legal-person-data-client-builder.class';
import { LegalPersonDataNonClientFormBuilder } from '@modules/business-accounts/forms/utils/data-step/legal-person/builders/legal-person-data-non-client-builder.class';
import { LegalPersonDataStepDirector } from '@modules/business-accounts/forms/utils/data-step/legal-person/director.class';
import { ConfigLegalPersonNoClientBuilder } from '@modules/business-accounts/forms/utils/legal-person-data-step/builders/legal-person-data-builder.class';
import { LegalPersonDataNoClientDirector } from '@modules/business-accounts/forms/utils/legal-person-data-step/director.class';
import { OtpBuilder } from '@modules/business-accounts/forms/utils/otp/builders/otp-builder.class';
import { OtpStepDirector } from '@modules/business-accounts/forms/utils/otp/director.class';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';
import { TrackingService } from '@modules/business-accounts/services/tracking/tracking.service';
import { VisitEvent } from '@modules/business-accounts/utils/classes/visit-event.class';
import { TraceEventName } from '@modules/business-accounts/utils/enums/trace-events.enum';
import { AccountCreatedEvent } from '@modules/business-accounts/utils/interfaces/account-created-event.interface';
import { BARoutes } from '@utils/constants/routes/cmp-link';
import { CustomerReniecResponse, ICompanyResponse } from '@utils/interfaces/response';
import { Observable, map, catchError, tap, switchMap, filter, mergeMap, timeout, delay } from 'rxjs';
import { IContainer } from '../utils/interfaces/container.interface';
import { DEFAULT_VIEW_CONFIG, ViewConfig } from '../utils/types/view-config.type';

@Component({
  selector: 'ba-legal-person-container',
  templateUrl: './legal-person-container.component.html',
  styleUrls: ['./legal-person-container.component.scss'],
})
export class LegalPersonContainerComponent extends BARoutes(DocumentTypes()) implements OnInit, IContainer {
  @Input() documentNumber!: string;
  @Input() legalRepresentantNumber!: string;
  @Input() ruc!: string;
  @Input() documentType!: Exclude<DocumentType, DocumentType.DNI>;

  @Output() userBlacklisted = new EventEmitter();
  @Output() viewConfigChange = new EventEmitter<ViewConfig>();
  @Output() processCompleted = new EventEmitter<AccountCreatedEvent[]>();
  @Output() flowCompleted = new EventEmitter();
  @Output() userMaxAccountsNumber = new EventEmitter();
  @Output() exitFlowCreateAccount = new EventEmitter<void>();

  showErrorDisclaimer = false;
  showWarnDisclaimer = false;
  isButtonDisabled = false;

  currentStep$!: Observable<number>;
  maxSteps: number = 5;

  company$!: ReturnType<CustomersFacade['fetchCompany']>;

  isClient: boolean = false;
  firstWindow: string = 'send-email';
  companyData!: any;
  accountsNumber: number = 0;

  dataForm?: FormGroup;
  otpForm?: FormGroup;
  configForm?: FormGroup;
  legalRepresentativeForm?: FormGroup;
  dataCollectionForm?: FormGroup;
  isLegalRepresentativeClient = false;

  isConfigutarionStepLoading = false;

  constructor(
    public readonly viewRouter: RouterService,
    private readonly host: ElementRef<HTMLDivElement>,
    private readonly customersFacade: CustomersFacade,
    private readonly traceService: TraceService,
    private trackingService: TrackingService,
    private readonly legalRepresentantService: LegalRepresentService,
    private readonly sessionService: SessionService
  ) {
    super();
  }

  ngOnInit(): void {
    this.company$ = this.customersFacade.fetchCompany(this.documentNumber).pipe(
      tap((res) => {
        this.traceService.setCommonInfo({
          isClient: !!res.company?.companyId,
          isLegalPerson: true,
        });
        this.isClient = !!res.company?.companyId;
        this.companyData = {
          society: res.company.societyType,
          constitutionDate: res.company.constitutionDate,
          companyId: res.company.companyId,
        };
      }),
      catchError((err) => {
        this.traceService.setCommonInfo({
          isClient: false,
          isLegalPerson: true,
        });
        this.isClient = false;
        this.maxSteps = 5;
        throw err;
      })
    );
    this.initializeCurrentStep();

    this.viewRouter.route$
      .pipe(
        filter(() => !!this.dataForm || !!this.legalRepresentativeForm),
        filter((route) => route === this.baRoutes.BUSINESS_DATA || route === this.baRoutes.REP_DATA)
      )
      .subscribe(() => {
        let { constitutionDate } = this.dataForm?.get('companyData')?.value;
        let { birthday } = this.legalRepresentativeForm?.value || '';

        if (constitutionDate && constitutionDate instanceof Date) {
          constitutionDate = constitutionDate.toLocaleString().split(',')[0].replace(/\/+/g, '-');
        }
        if (birthday && birthday instanceof Date) {
          birthday = birthday.toLocaleString().split(',')[0].replace(/\/+/g, '-');
        }

        if (constitutionDate) this.dataForm?.get('companyData')?.get('constitutionDate')?.setValue(constitutionDate);
        if (birthday) this.legalRepresentativeForm?.get('birthday')?.setValue(birthday);
      });
  }

  initializeDataForm() {
    if (this.dataForm) return;
    let builder: IBaseFormBuilder;
    if (this.isClient) {
      builder = new LegalPersonDataClientFormBuilder(this.sessionService, {
        companyData: this.companyData,
        isClient: true,
      });
    } else {
      builder = new LegalPersonDataNonClientFormBuilder(this.sessionService, {
        useCompanyData: !this.isClient,
        isClient: false,
      });
    }
    const director = new LegalPersonDataStepDirector(builder);
    director.make();
    this.dataForm = builder.getResult() as FormGroup;
  }

  initializeConfigForm() {
    const builder: IBaseFormBuilder = new ConfigLegalPersonBuilder();
    const director = new ConfigurationStepDirector(builder);
    director.make({ accountsNumber: this.accountsNumber });
    this.configForm = builder.getResult() as FormGroup;
  }

  initializeOtpForm() {
    const builder: IBaseFormBuilder = new OtpBuilder();
    const director = new OtpStepDirector(builder);

    director.make();
    this.otpForm = builder.getResult() as FormGroup;
  }

  initializeDataCollectionForm() {
    const builder: IBaseFormBuilder = new DataCollectionFormBuilder();
    const director = new DataCollectionpDirector(builder);

    director.make();
    this.dataCollectionForm = builder.getResult() as FormGroup;
  }

  initializeLegalRepresentativeForm() {
    const builder: IBaseFormBuilder = new ConfigLegalPersonNoClientBuilder(
      this.dataForm?.get('isSameAddressCompanyAndLegalRepresentant')?.value
    );
    const director = new LegalPersonDataNoClientDirector(builder);
    director.make();
    this.legalRepresentativeForm = builder.getResult() as FormGroup;
  }

  requestScrollTop() {
    this.host.nativeElement.dispatchEvent(
      new CustomEvent('scrollRequest', {
        detail: { dir: 'top' },
        bubbles: true,
      })
    );
  }

  initializeCurrentStep() {
    this.currentStep$ = this.viewRouter.route$.pipe(
      map((route) => {
        this.requestScrollTop();
        switch (route) {
          case this.baRoutes.DATA_COLLECTION:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_DATA_COLLECTION));
            this.initializeDataCollectionForm();
            return 0;
          case this.baRoutes.STARTUP:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_INITIAL));
            this.viewConfigChange.emit({
              showTitle: false,
              showBackButton: false,
              showDivider: false,
              disableStickyNav: true,
              showLogo: true,
            });
            return 1;
          case this.baRoutes.AUTHORIZE:
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Validación de identidad',
            });
            return 2;
          case this.baRoutes.BUSINESS_DATA:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_PERSONAL_DATA));
            this.initializeDataForm();
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Datos de la empresa',
              showBackButton: false,
            });
            return 3;
          case this.baRoutes.REP_DATA:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_LEGAL_PERSONAL_DATA));
            this.initializeLegalRepresentativeForm();
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Tus datos como representante legal',
            });
            return 4;
          case this.baRoutes.OTP:
            this.initializeOtpForm();
            return 4;
          case this.baRoutes.CONFIGURATION:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_ACCOUNT_DATA));
            this.initializeConfigForm();
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Configura tu nueva cuenta',
            });
            return this.maxSteps;
          case this.baRoutes.SUCCESS:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_SUCCESS));
            this.viewConfigChange.emit({
              showTitle: false,
              showBackButton: false,
              showDivider: false,
              disableStickyNav: true,
              showLogo: true,
            });
            return -1;
          default:
            return -1;
        }
      })
    );
  }

  shouldUsePartnersForm(company: ICompanyResponse['company']) {
    return company && (company.societyType === 'SAC' || company.societyType === 'SA');
  }

  onStartupStepDone() {
    this.viewRouter.navigate(this.baRoutes.AUTHORIZE);
  }

  onAuthorizationStepDone() {
    this.viewRouter.navigate(this.baRoutes.BUSINESS_DATA);
  }

  onDataStepDone() {
    this.customersFacade
      .fetchCustomer(this.legalRepresentantNumber, DocumentType.DNI)
      .pipe(switchMap((res) => this.customersFacade.fetchReniecData(this.legalRepresentantNumber)))
      .subscribe({
        next: ({ customer }: CustomerReniecResponse) => {
          this.legalRepresentantService.setLegalRepresentant = customer;
          this.isLegalRepresentativeClient = true;
          if (this.isLegalRepresentativeClient) this.viewRouter.navigate(this.baRoutes.OTP);
          else this.viewRouter.navigate(this.baRoutes.REP_DATA);
        },
        error: (error) => {
          this.viewRouter.navigate(this.baRoutes.REP_DATA);
          this.isLegalRepresentativeClient = false;
          console.error(error);
        },
      });
  }

  onLegalRepresentativeStepDone() {
    this.viewRouter.navigate(this.baRoutes.OTP);
  }

  onOtpStepDone() {
    this.viewRouter.navigate(this.baRoutes.CONFIGURATION);
  }

  getCompanyAddress() {
    let companyAddress: any = {
      country: 'PE',
    };
    if (!this.isClient) {
      companyAddress = {
        ...companyAddress,
        ...this.dataForm?.value.address,
        department: this.dataForm?.value.address.departmentName,
        province: this.dataForm?.value.address.provinceName,
        district: this.dataForm?.value.address.districtName,
      };
      delete companyAddress['departmentName'];
      delete companyAddress['provinceName'];
      delete companyAddress['districtName'];
    }
    return companyAddress;
  }

  getConstitutionDate() {
    let constitutionDate = null;
    if (this.dataForm?.value.companyData.constitutionDate instanceof Date)
      constitutionDate = this.dataForm?.value.companyData.constitutionDate;
    else {
      const fechaConstitutionDate = this.dataForm?.value.companyData.constitutionDate.split("-");
      const anio = fechaConstitutionDate[2];
      const month = parseInt(fechaConstitutionDate[1]) - 1
      const day = fechaConstitutionDate[0];
      constitutionDate = new Date(anio, month, day).toISOString();
    }
    return constitutionDate;
  }

  getCustomerAddress() {
    let customerAddress: any = {
      country: 'PE',
    };
    if (this.legalRepresentativeForm) {
      customerAddress = {
        ...customerAddress,
        ...this.legalRepresentativeForm.value.address,
        department: this.legalRepresentativeForm?.value.address?.departmentName,
        province: this.legalRepresentativeForm?.value.address?.provinceName,
        district: this.legalRepresentativeForm?.value.address?.districtName,
      };
      delete customerAddress['departmentName'];
      delete customerAddress['provinceName'];
      delete customerAddress['districtName'];
    }
    return customerAddress;
  }

  getPartnertList() {
    return (this.dataForm?.value.partnerList as Array<any>).map((partner) => {
      const partnerItem: any = {};
      console.log(partner);
      partnerItem.identityDocument = { type: partner.documentType, number: partner.documentNumber };
      if (partner.documentType === DocumentType.CE) {
        partnerItem.firstName = partner.CEData.firstName;
        partnerItem.lastName = partner.CEData.lastName;
        partnerItem.countryNationality = partner.CEData.nationality;
        partnerItem.countryBirth = partner.CEData.birthCountry;

        const address = {
          ...partner.CEData.address,
          department: partner.CEData.address?.departmentName,
          province: partner.CEData.address?.provinceName,
          district: partner.CEData.address?.districtName,
        };
        delete address['departmentName'];
        delete address['provinceName'];
        delete address['districtName'];

        partnerItem.address = address;
      }
      if (partner.pepPartnerList) {
        partnerItem.isPep = partner.isPEP;
        partnerItem.pepEntity = partner.pepPartnerList.pepEntity;
        partnerItem.pepPosition = partner.pepPartnerList.pepPosition;
        partnerItem.pepStartDate = partner.pepPartnerList.pepStartDate;
        partnerItem.pepEndDate = partner.pepPartnerList.pepEndDate;
      }
      return partnerItem;
    });
  }

  async onConfigurationStepDone() {
    this.isConfigutarionStepLoading = true;
    const {
      configData: { currency },
    } = this.configForm?.value;

    const companyAddress = this.getCompanyAddress();
    const customerAddress = this.getCustomerAddress();
    const payload: any = {
      currency,
      tariff: TariffType.DIGITAL,
      company: {
        identityDocument: {
          type: 'RUC',
          number: this.ruc,
        },
        sectorCode: this.dataForm?.value.sectorData.sectorCode,
        subSectorCode: this.dataForm?.value.sectorData.subSectorCode,
        businessName: this.dataForm?.value.companyData.businessName,
        mainActivity: 'BUSINESS',
        constitutionDate: this.getConstitutionDate(),
        email: { value: this.dataForm?.value.companyData.email, type: 'PERSONAL' },
        societyType: this.dataForm?.value.companyData.societyType,
        registrationNumber: this.dataForm?.value.registrationNumber,
      },
      store: { storeCode: this.configForm?.value.storeData.storeCode },
      agency: {
        code: this.configForm?.value.agencyData.departmentCode,
        name: this.configForm?.value.agencyData.department,
      },
    };

    if (this.dataForm?.value.partnerList) {
      payload.partners = this.getPartnertList();
    }

    if (this.isClient) payload.company.companyId = this.companyData?.companyId;
    if (!this.isClient) {
      payload.company.address = companyAddress;
    }

    if (this.isLegalRepresentativeClient) {
      const customer = this.legalRepresentantService.legalRepresentantValue!;

      payload.customer = {
        identityDocument: [{ type: 'DNI', number: this.legalRepresentantNumber }],
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone?.map((phone) => ({
          ...phone,
          number: phone.number?.replace(/\s+/g, ''),
          carrier: 'ENTEL',
        })),
        email: customer.email?.map((email) => ({ ...email, type: 'PERSONAL' })),
        ldpd: { accepted: this.configForm?.value.personalData || false },
        maritalStatus: customer.maritalStatus,
        birthDate: customer.birthDate,
        gender: customer.gender,
        address: customer.address,
        pep: {
          accepted: this.legalRepresentativeForm?.value.personalData.isPEP || false,
          pepEntity: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepEntity || '',
          pepPosition: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepPosition || '',
          pepStartDate: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepStartDate || null,
          pepEndDate: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepEndDate || null,
        }
      };
    }

    if (!this.isLegalRepresentativeClient) {
      const customer = this.legalRepresentantService.legalRepresentantValue;
      payload.customer = {
        firstName: customer?.firstName,
        lastName: customer?.lastName,
        motherLastName: customer?.motherLastName,
        gender: this.legalRepresentativeForm?.value.gender,
        birthDate: this.legalRepresentativeForm?.value.birthday,
        maritalStatus: this.legalRepresentativeForm?.value.maritalStatus,

        identityDocument: [{ type: 'DNI', number: this.legalRepresentantNumber }],
        email: [{ value: this.legalRepresentativeForm?.value.email, type: 'PERSONAL' }],
        phone: [
          {
            type: 'MOBILE',
            number: this.legalRepresentativeForm?.value.phoneNumber.replace(/\s+/g, ''),
            carrier: this.legalRepresentativeForm?.value.phoneCarrier,
          },
        ],
        address: [this.legalRepresentativeForm?.get('isSameAddress')?.value ? companyAddress : customerAddress],
        ldpd: { accepted: this.configForm?.value.personalData || false },
        pep: {
          accepted: this.legalRepresentativeForm?.value.personalData.isPEP || false,
          pepEntity: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepEntity || '',
          pepPosition: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepPosition || '',
          pepStartDate: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepStartDate || null,
          pepEndDate: this.legalRepresentativeForm?.value.pepLegalRepresentative?.pepEndDate || null,
        }
      };
    }
    if (!payload.customer.email) {
      this.showErrorDisclaimer = true;
      this.isButtonDisabled = true;
      this.configForm?.disable();
      return;
    }
    payload.deviceType = this.trackingService.getDeviceInfo();

    console.log('Payload => ', payload);

    const subscription = this.customersFacade
      .createBussinessAccount(payload)
      .pipe(
        catchError((err) => {
          console.log('Error => ', err);
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          throw err;
        }),
        delay(3000),
        mergeMap((res: any) => this.customersFacade.getCorrelationStatus(res.correlationId)),
        timeout(600000),
        catchError((err) => {
          console.log('Error => ', err);
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          throw err;
        })
      )
      .subscribe((response: any) => {
        if (response.correlation.status === 'SETTLED') {
          if (response.correlation.data.accounts) {
            const accountsInfo: AccountCreatedEvent[] = [];

            response.correlation.data.accounts.forEach((account: any) => {
              accountsInfo.push({
                currency: account.currency,
                accountId: account.id,
                status: response.correlation.status,
                accountCorrelationId: response.correlation.id,
              });
            });

            this.processCompleted.emit(accountsInfo);
            this.viewRouter.navigate(this.baRoutes.SUCCESS);
            subscription.unsubscribe();
          }
        }

        if (response.correlation.status === 'FAILED') {
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          subscription.unsubscribe();
        }
      });
  }

  onSuccessStepDone() {
    this.dataForm?.reset();
    this.legalRepresentativeForm?.reset();
    this.flowCompleted.emit();
    this.viewRouter.navigate(this.baRoutes.STARTUP);
  }
}
